import { Box, Button, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import React, { useEffect, useState } from "react";
// cspell:ignore qrcode
import { QRCodeSVG } from "qrcode.react";
import ModalComponent from "./modalComponent";
import { convertToIndianFormat } from "../../utils/functions";
import { copyTextToClipboard } from "../../utils/clipboard";

// pa is left unencoded since some UPI apps don't decode "%40" in the VPA
export const buildUPIString = (amount, transactionRef) => {
    if (!transactionRef) return "";
    return `upi://pay?pa=realx@idfcbank&pn=REALX%20MARKETPLACE%20PVT%20LIMITED&mc=6513&tr=${transactionRef}&cu=INR`;
}

const getSecondsLeft = (expiryTime) => {
  if (!expiryTime) return null;

  // expiryTime is a Unix timestamp in seconds
  const expiry = Number(expiryTime) * 1000;

  if (Number.isNaN(expiry)) return null;

  return Math.max(0, Math.floor((expiry - Date.now()) / 1000));
};

const formatCountdown = (seconds) => {
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
}

export default function UPIPaymentModal({ open, handleClose = () => { }, amount = 0, expiryTime = null, transactionRef, onSubmit = () => { }, onExpiredClose = () => { }, loading = false }) {
    const payAmount = Number(amount) || 0;
    // derived from expiryTime on every render so a previous request's countdown never leaks into a new one
    const [, setTick] = useState(0);
    const [amountCopied, setAmountCopied] = useState(false);
    const theme = useTheme();
    // phones only; tablets keep the QR flow since they are usually not the device holding the UPI app
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const secondsLeft = getSecondsLeft(expiryTime);
    const hasDeadline = secondsLeft !== null;
    const isExpired = hasDeadline && secondsLeft <= 0;

    useEffect(() => {
        if (!open || !expiryTime) return;
        const timer = setInterval(() => {
            setTick((t) => t + 1);
            if (getSecondsLeft(expiryTime) <= 0) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [open, expiryTime]);

    useEffect(() => {
        if(open && !transactionRef) handleClose();
    }, [open, transactionRef])

    const submit = () => {
        onSubmit();
        handleClose();
    }

    const copyAmount = async () => {
        try {
            await copyTextToClipboard(String(payAmount));
            setAmountCopied(true);
            setTimeout(() => setAmountCopied(false), 2000);
        } catch { }
    }

    // only an expired request can be dismissed; onExpiredClose lets the parent drop the stale transaction
    const handleCloseWrapper = () => {
        if (!isExpired) return;
        handleClose();
        onExpiredClose();
    }

    const upiLink = (open && transactionRef && !isExpired) ? buildUPIString(payAmount, transactionRef) : null;
    const timerColor = secondsLeft <= 60 ? "error" : "primary";

    const expiryNote = isExpired
        ? "This transaction has been cancelled automatically. If any amount was debited from your account, please reach out to our support team."
        : isMobile
            ? "Once the payment is successful in your UPI app, return here and tap \"I've completed the payment\" before the timer runs out. Unconfirmed payments are cancelled automatically."
            : "Please complete the payment and tap Submit before the timer runs out. Payments not confirmed within this window will be cancelled automatically.";

    const mobileContent = (
        <Stack spacing={2.5} alignItems={"stretch"} textAlign={"center"} sx={{ width: "100%" }}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
                <Typography variant="body2" color="text.secondary">Amount payable</Typography>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={0.5}>
                    <Typography variant="h4" fontWeight={700}>{convertToIndianFormat(payAmount)}</Typography>
                    <Tooltip title={amountCopied ? "Copied" : "Copy amount"}>
                        <IconButton size="small" onClick={copyAmount} aria-label="Copy amount">
                            {amountCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary">to REALX Marketplace Pvt Limited</Typography>
            </Box>
            {hasDeadline && (
                <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
                    <TimerOutlinedIcon fontSize="small" color={timerColor} />
                    <Typography variant="body2" color="text.secondary">Request expires in</Typography>
                    <Typography variant="subtitle1" fontWeight={700} color={timerColor} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCountdown(secondsLeft)}
                    </Typography>
                </Stack>
            )}
            <Stack spacing={1}>
                <Button component="a" href={upiLink} fullWidth size="large" variant="contained" sx={{ py: 1.5 }}>
                    {/* single span so the flex button doesn't collapse the spaces around the amount span */}
                    <span>Pay {convertToIndianFormat(payAmount)} via UPI app</span>
                </Button>
                <Typography variant="caption" color="text.secondary">
                    Opens Google Pay, PhonePe, Paytm or any UPI app on your phone
                </Typography>
            </Stack>
            <Button onClick={submit} disabled={loading} fullWidth size="large" variant="outlined">
                I've completed the payment
            </Button>
            <Typography variant="caption" color="text.secondary">{expiryNote}</Typography>
        </Stack>
    );

    const desktopContent = (
        <>
            <Typography variant="h5">Scan the QR code with any UPI app to pay {convertToIndianFormat(payAmount)}</Typography>
            <Box sx={{ p: 2, bgcolor: "#FFF", borderRadius: 1 }}>
                <QRCodeSVG value={upiLink} size={200} />
            </Box>
            {hasDeadline && (
                <Stack spacing={1} alignItems={"center"} textAlign={"center"}>
                    <Typography variant="body2" color="text.secondary">Complete your payment within</Typography>
                    <Typography variant="h4" fontWeight={600} color={timerColor} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCountdown(secondsLeft)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>{expiryNote}</Typography>
                </Stack>
            )}
            <Box sx={{ width: "100%", px: { xs: 8, md: 10 } }}>
                <Button onClick={submit} disabled={loading} fullWidth variant="contained">Submit</Button>
            </Box>
        </>
    );

    const expiredContent = (
        <>
            <Stack spacing={1} alignItems={"center"} textAlign={"center"}>
                <Typography variant="h7" color="error">This UPI payment request has expired!</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>{expiryNote}</Typography>
            </Stack>
            <Box sx={{ width: "100%", px: { xs: 0, sm: 8, md: 10 } }}>
                <Button onClick={handleCloseWrapper} fullWidth variant="contained">Close</Button>
            </Box>
        </>
    );

    return (
        <ModalComponent open={open} hideClose={true} handleClose={handleCloseWrapper} title={isMobile ? "Pay via UPI" : "UPI Payment"} hideTitle={isExpired} fullWidth={isMobile}>
            <Stack mt={isMobile ? 2 : 1} spacing={2.5} alignItems={"center"}>
                {isExpired ? expiredContent : isMobile ? mobileContent : desktopContent}
            </Stack>
        </ModalComponent>
    )
}
